import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { RegionMapInfo, AccountedGenerationInfo, RegionOwnerMapInfo } from './config'
import { formatVolume, formatVolumeInteger } from './helpers'
import style from './style.m.less'

type TooltipBodyProps = {
    mapMode: number
    locale: 'ru' | 'en'
    regionInfo: RegionMapInfo | null
    accountedInfo: AccountedGenerationInfo | null
    ownerInfo?: RegionOwnerMapInfo | null
    type?: number
    isEmpty: boolean
    energyTypesOptions?: any[]
}

const TooltipBody: FC<TooltipBodyProps> = ({
    mapMode,
    locale,
    regionInfo,
    accountedInfo,
    ownerInfo,
    type,
    isEmpty,
    energyTypesOptions,
}) => {
    const { t } = useTranslation()
    let volumeUnit = t('units.power.MW', { defaultValue: locale === 'ru' ? 'МВт' : 'mW' })

    if (mapMode === 0) {
        if (isEmpty) {
            return <div className={style.tooltipEmpty}>{t('main.mapNoQualified')}</div>
        }

        if (!regionInfo) {
            return null
        }

        return (
            <div className={style.tooltipBody}>
                <div className={style.tooltipRow}>
                    <span className={style.tooltipLabel}>{t('main.mapRegCount')}:</span>
                    <span className={style.tooltipValue}>{regionInfo.reg_count}</span>
                </div>
                <div className={style.tooltipRow}>
                    <span className={style.tooltipLabel}>{t('main.mapRegVolume')}:</span>
                    <span className={style.tooltipValue}>
                        {formatVolume(regionInfo.reg_volume, locale, volumeUnit)}
                    </span>
                </div>
                <div className={style.tooltipRow}>
                    <span className={style.tooltipLabel}>{t('main.mapQualCount')}:</span>
                    <span className={style.tooltipValue}>{regionInfo.qualified_count}</span>
                </div>
                <div className={style.tooltipRow}>
                    <span className={style.tooltipLabel}>{t('main.mapQualVolume')}:</span>
                    <span className={style.tooltipValue}>
                        {formatVolume(regionInfo.qualified_volume, locale, volumeUnit)}
                    </span>
                </div>
            </div>
        )
    }

    if (mapMode === 1) {
        volumeUnit = t('units.power.MW', { defaultValue: locale === 'ru' ? 'МВт*ч' : 'mW*h' })
        if (isEmpty) {
            return <div className={style.tooltipEmpty}>{t('main.mapNoAttributes')}</div>
        }

        if (!accountedInfo) {
            return null
        }

        return (
            <div className={style.tooltipBody}>
                <div className={style.tooltipRow}>
                    <span className={style.tooltipLabel}>{t('main.mapTotalVolume')}:</span>
                    <span className={style.tooltipValue}>
                        {formatVolumeInteger(accountedInfo.total_volume, locale, volumeUnit)}
                    </span>
                </div>
                {accountedInfo.energy_types?.map((type) => {
                    const option = energyTypesOptions?.find(
                        (opt) => opt.id === type.renewable_energy_type_id,
                    )
                    const label =
                        option?.texts?.[locale] ?? option?.text ?? type.renewable_energy_type_id

                    return (
                        <div className={style.tooltipRow} key={type.renewable_energy_type_id}>
                            <span className={style.tooltipLabel}>{label}:</span>
                            <span className={style.tooltipValue}>
                                {formatVolumeInteger(type.volume, locale, volumeUnit)}
                            </span>
                        </div>
                    )
                })}
            </div>
        )
    }

    if (mapMode === 2) {
        volumeUnit = t('units.power.MW', { defaultValue: locale === 'ru' ? 'МВт' : 'MW' })
        if (isEmpty) {
            return <div className={style.tooltipEmpty}>{t('main.mapNoOwners')}</div>
        }

        if (!ownerInfo) {
            return null
        }

        return (
            <div className={style.tooltipBody} style={{ width: 'max-content' }}>
                {ownerInfo.energy_types?.map((et) => {
                    if (type !== 0 && type !== et.energy_type_id) {
                        return null
                    }
                    if (!et.owners || et.owners.length === 0) {
                        return null
                    }

                    const option = energyTypesOptions?.find((opt) => opt.id === et.energy_type_id)
                    const label = option?.texts?.[locale] ?? option?.text ?? et.energy_type_id

                    const ownersList = [...et.owners].sort(
                        (a, b) => b.total_power_mw - a.total_power_mw,
                    )

                    return (
                        <div key={et.energy_type_id}>
                            <div
                                className={style.tooltipRow}
                                style={{ marginTop: '4px', marginBottom: '4px' }}
                            >
                                <span className={style.tooltipOwner}>
                                    <strong>{label}</strong>
                                </span>
                            </div>
                            {ownersList.map((owner, idx) => (
                                <div
                                    key={idx}
                                    className={style.tooltipRow}
                                    style={{ marginTop: '3px' }}
                                >
                                    <span className={style.tooltipOwner}>
                                        {owner.owner_name}
                                        {' - '}
                                        {formatVolume(
                                            owner.total_power_mw * 1000,
                                            locale,
                                            volumeUnit,
                                        )}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )
                })}
            </div>
        )
    }

    return null
}

export default TooltipBody
